import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getProjectTasks, getTaskCategories } from '../../Requests/TaskRequests';
import UpgradedLoader from '../UpgradedLoader';

function ProjectGanttWidget({ project, preloadedTasks = null, preloadedCategories = null }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);
  
  // Auto-scroll container refs
  const scrollContainerRef = useRef(null);
  const timelineContentRef = useRef(null);

  useEffect(() => {
    if (!project?.uuid) return;

    // Use preloaded data if available (performance optimization)
    if (preloadedTasks && preloadedCategories) {
      try {
        setLoading(true);
        setError(null);
        
        setTasks(preloadedTasks || []);
        setCategories(preloadedCategories || []);
      } catch (err) {
        console.error('Error processing preloaded Gantt data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    } else {
      // Fallback to API calls if no preloaded data
      const fetchData = async () => {
        try {
          setLoading(true);
          setError(null);

          // Get both tasks and categories concurrently
          // Use team_uuid for proper permission checking
          const teamUuid = project.team_uuid || project.team_id;
          
          const [tasksResponse, categoriesResponse] = await Promise.all([
            getProjectTasks(project.uuid), // Use UUID instead of ID for route model binding
            getTaskCategories(teamUuid)
          ]);

          setTasks(tasksResponse.data?.data || []);
          setCategories(categoriesResponse.data || []);
        } catch (err) {
          console.error('Error fetching Gantt data:', err);
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    }
  }, [project?.uuid, preloadedTasks?.length, preloadedCategories?.length]); // Simplified dependencies to prevent loops

  // Auto-scroll to center on the current month
  useEffect(() => {
    if (scrollContainerRef.current && categories.length > 0) {
      const container = scrollContainerRef.current;
      
      // Calculate timeline range locally to avoid dependency issues
      const currentTimelineRange = (() => {
        let earliestStart = null;
        const timelineData = categories.length > 0 ? categories : [
          { id: 1, name: 'Demo Category', color: '#3B82F6', start_date: null, end_date: null }
        ];

        timelineData.forEach(category => {
          if (category.start_date) {
            const startDate = new Date(category.start_date);
            if (!earliestStart || startDate < earliestStart) {
              earliestStart = startDate;
            }
          }
        });

        if (!earliestStart) {
          earliestStart = new Date('2026-01-01');
        }

        const mainStart = new Date(earliestStart.getFullYear(), earliestStart.getMonth(), 1);
        const extendedStart = new Date(mainStart.getFullYear(), mainStart.getMonth() - 6, 1);
        
        return { extendedStart };
      })();
      
      // Find current month index in the timeline
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      
      const months = [];
      const current = new Date(currentTimelineRange.extendedStart);
      
      // Generate month list to find current month index
      for (let i = 0; i < 18; i++) {
        months.push({
          month: current.getMonth(),
          year: current.getFullYear(),
          index: i
        });
        current.setMonth(current.getMonth() + 1);
      }
      
      // Find the index of the current month
      const currentMonthIndex = months.findIndex(m => 
        m.month === currentMonth && m.year === currentYear
      );
      
      if (currentMonthIndex !== -1) {
        // Each month is 100px wide, center the current month
        const monthWidth = 100;
        const currentMonthPosition = currentMonthIndex * monthWidth;
        const centerOffset = container.clientWidth / 2 - monthWidth / 2;
        container.scrollLeft = currentMonthPosition - centerOffset + 160; // +160 for label space (w-32 pr-4 = 128+16)
      } else {
        // Fallback to center on main range if current month not found
        // Find the middle of the main range
        const mainRangeStart = currentTimelineRange.extendedStart;
        const mainStart = new Date(mainRangeStart.getFullYear(), mainRangeStart.getMonth() + 6, 1); // 6 months into extended range
        const mainStartMonthIndex = Math.round((mainStart.getTime() - mainRangeStart.getTime()) / (30 * 24 * 60 * 60 * 1000));
        const mainStartPosition = mainStartMonthIndex * 100;
        container.scrollLeft = mainStartPosition - (container.clientWidth / 2) + 160;
      }
    }
  }, [categories]);

  // Synchronize horizontal scroll between header and timeline content
  useEffect(() => {
    if (loading || error) return;
    
    const headerContainer = scrollContainerRef.current;
    const timelineContainer = timelineContentRef.current;
    
    if (!headerContainer || !timelineContainer) return;

    // Sync timeline content when header scrolls
    const handleHeaderScroll = (e) => {
      const scrollLeft = e.target.scrollLeft;
      // Account for category column offset (144px)
      timelineContainer.scrollLeft = scrollLeft;
    };

    // Sync header when timeline content scrolls  
    const handleTimelineScroll = (e) => {
      const scrollLeft = e.target.scrollLeft;
      // Account for category column offset (144px) 
      headerContainer.scrollLeft = scrollLeft;
    };

    headerContainer.addEventListener('scroll', handleHeaderScroll);
    timelineContainer.addEventListener('scroll', handleTimelineScroll);
    
    return () => {
      headerContainer.removeEventListener('scroll', handleHeaderScroll);
      timelineContainer.removeEventListener('scroll', handleTimelineScroll);
    };
  }, [loading, error, categories.length]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <UpgradedLoader className="animate-spin" w={6} h={6} bg="bg-iavisus-100" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-red-500 text-sm">{t('Common.ErrorOccurred')}</p>
        </div>
      </div>
    );
  }

  // Debug: Log the data we received
  
  // Use categories to create a timeline with real date calculations
  const timelineData = categories.length > 0 ? categories : [
    { id: 1, name: 'Demo Category', color: '#3B82F6', start_date: null, end_date: null }
  ];

  // Calculate extended timeline range (6 months before + 6 visible + 6 after = 18 months total)
  const getExtendedTimelineRange = () => {
    let earliestStart = null;
    let latestEnd = null;

    timelineData.forEach(category => {
      if (category.start_date) {
        const startDate = new Date(category.start_date);
        if (!earliestStart || startDate < earliestStart) {
          earliestStart = startDate;
        }
      }
      if (category.end_date) {
        const endDate = new Date(category.end_date);
        if (!latestEnd || endDate > latestEnd) {
          latestEnd = endDate;
        }
      }
    });

    // Default dates if no dates found
    if (!earliestStart) {
      earliestStart = new Date('2026-01-01');
    }
    if (!latestEnd) {
      latestEnd = new Date('2026-12-31');
    }

    // Calculate the date range from earliest start to latest end
    const mainStart = new Date(earliestStart.getFullYear(), earliestStart.getMonth(), 1);
    const mainEndFromLatest = new Date(latestEnd.getFullYear(), latestEnd.getMonth() + 1, 0); // End of latest month
    
    // Ensure we have at least 6 months visible, but extend if data goes beyond
    const minMainEnd = new Date(mainStart.getFullYear(), mainStart.getMonth() + 5, 0); // 6 months minimum
    const mainEnd = mainEndFromLatest > minMainEnd ? mainEndFromLatest : minMainEnd;

    // Extended range: 6 months before main start + main range + 6 months after main end
    const extendedStart = new Date(mainStart.getFullYear(), mainStart.getMonth() - 6, 1);
    const extendedEnd = new Date(mainEnd.getFullYear(), mainEnd.getMonth() + 6, 0);

    return { 
      extendedStart, 
      extendedEnd, 
      mainStart, 
      mainEnd,
      visibleRange: { start: mainStart, end: mainEnd }
    };
  };

  const timelineRange = getExtendedTimelineRange();

  // Generate month headers for the full scrollable timeline
  const getExtendedMonthHeaders = () => {
    const months = [];
    const current = new Date(timelineRange.extendedStart);
    
    // Generate months from extendedStart to extendedEnd
    while (current <= timelineRange.extendedEnd) {
      const isMainMonth = current >= timelineRange.mainStart && current <= timelineRange.mainEnd;
      months.push({
        label: current.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        date: new Date(current),
        isMainMonth // Mark which months are in the main visible range
      });
      current.setMonth(current.getMonth() + 1);
    }
    
    return months;
  };

  const monthHeaders = getExtendedMonthHeaders();


  // Calculate timeline positioning based on month-aligned pixels (100px per month)
  const calculateTimelinePosition = (category) => {
    const MONTH_WIDTH = 100; // Each month is 100px wide

    if (!category.start_date || !category.end_date) {
      // Fallback for categories without dates - position in main range
      const mainStartMonths = getMonthDifference(timelineRange.extendedStart, timelineRange.mainStart);
      const leftPixels = mainStartMonths * MONTH_WIDTH + 20; // 20px offset
      return { left: `${leftPixels}px`, width: '150px' };
    }

    const startDate = new Date(category.start_date);
    const endDate = new Date(category.end_date);

    // Calculate position in months from timeline start
    const startMonthsFromBeginning = getMonthDifference(timelineRange.extendedStart, startDate);
    const durationMonths = getMonthDifference(startDate, endDate);
    
    // Convert to pixels (each month = 100px)
    const leftPixels = Math.max(0, startMonthsFromBeginning * MONTH_WIDTH);
    const widthPixels = Math.max(50, durationMonths * MONTH_WIDTH); // Minimum 50px width

    return {
      left: `${leftPixels}px`,
      width: `${widthPixels}px`
    };
  };

  // Helper function to calculate difference in months between two dates
  const getMonthDifference = (startDate, endDate) => {
    const start = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
    
    const yearDiff = end.getFullYear() - start.getFullYear();
    const monthDiff = end.getMonth() - start.getMonth();
    
    return yearDiff * 12 + monthDiff;
  };

  return (
    <div className="w-full bg-white">
      {/* Fixed header with month headers */}
      <div className="flex mb-4 px-4">
        {/* Space for category column */}
        <div className="w-36 pr-4 flex-shrink-0"></div>
        {/* Scrollable month headers */}
        <div 
          ref={scrollContainerRef}
          className="flex-1 overflow-x-auto [&::-webkit-scrollbar]:hidden"
          style={{ 
            scrollbarWidth: 'none', 
            msOverflowStyle: 'none'
          }}
        >
          <div className="flex" style={{ width: `${monthHeaders.length * 100}px` }}>
            {monthHeaders.map((month, index) => (
              <div 
                key={`${month.label}-${index}`}
                className={`text-xs text-center px-1 h-6 flex items-center justify-center ${
                  month.isMainMonth 
                    ? 'text-gray-800 font-medium' 
                    : 'text-gray-400'
                }`}
                style={{ width: '100px' }}
              >
                {month.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Synchronized vertical and horizontal scroll area with overlay categories */}
      <div className="relative" style={{ height: `${Math.max(200, timelineData.length * 48 + 32)}px` }}>
        {/* Category names overlay - positioned absolutely to stay on top */}
        <div className="absolute left-0 top-0 z-50 bg-white w-36 h-full px-4 border-r-2 border-gray-200">
          {timelineData.map((category, index) => (
            <div key={`cat-${category.id || index}`} className="flex items-center h-10 mb-2">
              <div className="flex items-center">
                <div 
                  className="w-3 h-3 rounded-full mr-2" 
                  style={{ backgroundColor: category.color || '#3B82F6' }}
                />
                <span className="text-xs sm:text-sm font-medium text-gray-700 truncate">
                  {category.name}
                </span>
              </div>
            </div>
          ))}
        </div>
        
        {/* Timeline content - positioned with left margin to account for overlay */}
        <div 
          ref={timelineContentRef}
          className="overflow-auto ml-36 [&::-webkit-scrollbar]:hidden"
          style={{ 
            scrollbarWidth: 'none', 
            msOverflowStyle: 'none'
          }}
        >
          <div style={{ width: `${monthHeaders.length * 100}px` }}>
            {timelineData.map((category, index) => {
              const position = calculateTimelinePosition(category);
              return (
                <div key={category.id || index} className="h-10 mb-2 flex items-center">
                  <div className="timeline-track relative h-8 bg-gray-100 rounded w-full">
                    {/* Main range indicator */}
                    <div 
                      className="absolute top-0 bottom-0 bg-blue-50 opacity-50 rounded"
                      style={{ 
                        left: `${((timelineRange.mainStart.getTime() - timelineRange.extendedStart.getTime()) / (timelineRange.extendedEnd.getTime() - timelineRange.extendedStart.getTime())) * 100}%`,
                        width: `${((timelineRange.mainEnd.getTime() - timelineRange.mainStart.getTime()) / (timelineRange.extendedEnd.getTime() - timelineRange.extendedStart.getTime())) * 100}%`
                      }}
                    />
                    {/* Category timeline bar */}
                    <div 
                      className="timeline-bar absolute top-1 bottom-1 rounded flex items-center"
                      style={{ 
                        backgroundColor: category.color || '#3B82F6',
                        left: position.left,
                        width: position.width,
                        opacity: 0.9,
                        zIndex: 1
                      }}
                    >
                      <span 
                        className="text-xs font-semibold ml-2 truncate"
                        style={{ 
                          color: '#ffffff',
                          textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                          filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.5))'
                        }}
                      >
                        {category.name}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// Memoize component to prevent unnecessary re-renders
export default React.memo(ProjectGanttWidget, (prevProps, nextProps) => {
    return prevProps.project?.uuid === nextProps.project?.uuid && 
           prevProps.preloadedTasks === nextProps.preloadedTasks &&
           prevProps.preloadedCategories === nextProps.preloadedCategories;
});